
import { skillIconNode } from "./itemicon";
import { elem, styleInstaller } from "./chrome";

/** One live modifier. Mirrors `Affect_` in `net/connection.ts`, which mirrors
 *  `AffectSlot` on the wire. */
export interface AffectModel {
  readonly source: number;
  readonly apply: number;
  readonly value: number;
  /** Ticks left when the frame was sent. Never a deadline. */
  readonly remaining: number;
}

/** What the panel needs from the skill bar to name and draw a source. */
export interface AffectSource {
  readonly vnum: number;
  readonly name: string;
  readonly icon: string;
}

/** The mall affect type, whose shower images key by point. */
export const AFFECT_MALL = 510; // AFFECT_MALL (affect.h:66)

const APPLY_NAMES: ReadonlyMap<number, string> = new Map([
  [12, "Strength"],
  [14, "Dexterity"],
  [17, "Attack Speed"],
  [19, "Moving Speed"],
  [21, "Casting Speed"],
  [40, "Critical Hit"],
  [41, "Piercing Hit"],
  [95, "Attack Value"],
  [96, "Defence"],
]);

const SKILL_ICONS: ReadonlyMap<number, string> = new Map([
  [3, "jeongwi"],
  [4, "geomgyeong"],
  [19, "cheongeun"],
  [34, "eunhyeong"],
  [49, "gyeonggong"],
  [63, "gwigeom"],
  [64, "gongpo"],
  [65, "jumagap"],
  [66, "pabeop"],
  [78, "muyeong"],
  [79, "heuksin"],
  [94, "hosin"],
  [95, "boho"],
  [96, "gicheon"],
  [110, "kwaesok"],
  [111, "jeungryeok"],
]);

const SKILL_NAMES: ReadonlyMap<number, string> = new Map([
  [3, "Berserk"],
  [4, "Aura of the Sword"],
  [19, "Strong Body"],
  [34, "Stealth"],
  [49, "Feather Walk"],
  [63, "Enchanted Blade"],
  [64, "Fear"],
  [65, "Enchanted Armour"],
  [66, "Dispel"],
  [78, "Flame Spirit"],
  [79, "Dark Protection"],
  [94, "Blessing"],
  [95, "Reflect"],
  [96, "Dragon's Strength"],
  [110, "Swiftness"],
  [111, "Attack Up"],
]);

const SOURCE_ICONS: ReadonlyMap<number, string> = new Map([
  [200, "/ui/affect/increase_move_speed.png"],
  [201, "/ui/affect/increase_attack_speed.png"],
  [211, "/ui/affect/slow.png"],
  [513, "/ui/affect/gold_premium.png"],
  [516, "/ui/affect/exp_bonus.png"],
]);

const SOURCE_NAMES: ReadonlyMap<number, string> = new Map([
  [200, "Moving speed raised"],
  [201, "Attack speed raised"],
  [211, "Slow"],
  [513, "No skill-book delay"],
  [516, "Ability Up"],
]);

const APPLY_ICONS: ReadonlyMap<number, string> = new Map([
  [17, "/ui/affect/increase_attack_speed.png"],
  [19, "/ui/affect/increase_move_speed.png"],
  [40, "/ui/affect/critical.png"],
  [41, "/ui/affect/gold_premium.png"],
]);

/** What this affect modifies, or null for one this build has no name for. */
export function applyName(apply: number): string | null {
  return APPLY_NAMES.get(apply) ?? null;
}

/** Shower raster URL for a wire affect, or null when only a glyph remains. */
export function affectRasterUrl(affect: AffectModel): string | null {
  if (SOURCE_ICONS.has(affect.source)) {
    if (affect.source === 516) {
      const byApply = APPLY_ICONS.get(affect.apply);
      if (byApply !== undefined) return byApply;
    }
    return SOURCE_ICONS.get(affect.source) ?? null;
  }
  return null;
}

/** Skill-sheet icon name for a skill-vnum source, independent of the bar. */
export function skillIconName(source: number): string | null {
  return SKILL_ICONS.get(source) ?? null;
}

export function signed(value: number): string {
  return value >= 0 ? `+${value}` : String(value);
}

/** Seconds a player would say are left. Rounded up, so a live affect never
 *  reads "0s". */
export function secondsLeft(ticks: number, tickHz: number): number {
  if (!Number.isFinite(ticks) || ticks <= 0) return 0;
  if (!Number.isFinite(tickHz) || tickHz <= 0) return 0;
  return Math.ceil(ticks / tickHz);
}

/** How long a duration reads on the chip. Minutes past ninety seconds, because
 *  a three-digit second count is harder to read at a glance than "2m". */
export function durationLabel(ticks: number, tickHz: number): string {
  const seconds = secondsLeft(ticks, tickHz);
  if (seconds <= 0) return "";
  if (seconds <= 90) return `${seconds}s`;
  return `${Math.ceil(seconds / 60)}m`;
}

export function describe(
  affect: AffectModel,
  sources: ReadonlyMap<number, AffectSource>,
  tickHz: number,
): string {
  const source = sources.get(affect.source);
  const what = applyName(affect.apply);
  const change = what === null ? "" : `${what} ${signed(affect.value)}`;
  const left = durationLabel(affect.remaining, tickHz);
  const named =
    source?.name ??
    SOURCE_NAMES.get(affect.source) ??
    SKILL_NAMES.get(affect.source) ??
    null;
  const head = named ?? (change || "Effect");
  const parts = [head];
  if (named && change) parts.push(change);
  if (left) parts.push(left);
  return parts.join(" · ");
}

/** Index a skill bar by vnum, for `describe` and the icon lookup. */
export function sourcesFrom(
  slots: readonly AffectSource[],
): ReadonlyMap<number, AffectSource> {
  return new Map(slots.map((slot) => [slot.vnum, slot]));
}

const STYLE_ID = "metin3-affects-style";

const CSS = `
.affects {
  position: absolute;
  left: 10px;
  top: 10px;
  display: flex;
  gap: 1px;
  pointer-events: none;
  z-index: 20;
}
.affect {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 2px;
  border: 1px solid rgba(216, 178, 106, 0.45);
  background: rgba(12, 10, 8, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
/* Original shower uses SetScale(0.7) on 32px cells with IMAGE_STEP 25. */
.affect .skill-icon,
.affect .affect-icon {
  width: 22px;
  height: 22px;
  image-rendering: pixelated;
}
.affect-icon {
  display: block;
  pointer-events: none;
}
.affect-left {
  position: absolute;
  bottom: 0;
  right: 1px;
  font: 600 9px/1 ui-monospace, monospace;
  color: #f2e2c0;
  text-shadow: 0 1px 2px #000;
}
/* Something is modifying you and this build cannot draw it. Better than an
   empty square: the player can at least count it. */
.affect-unknown {
  font: 600 11px/1 ui-monospace, monospace;
  color: #d8b26a;
}
`;

const installStyle = styleInstaller(STYLE_ID, CSS);

function rasterNode(src: string, doc: Document): HTMLElement {
  const img = elem(doc, "img", "affect-icon");
  img.src = src;
  img.alt = "";
  img.draggable = false;
  return img;
}

function glyphNode(affect: AffectModel, doc: Document): HTMLElement {
  const mark = elem(doc, "span", "affect-unknown");
  // The first letter of what it modifies, or a dot when even that is
  // unknown. Never the raw `apply` number: a player reading "95" learns
  // nothing and might think it is the value.
  const what = applyName(affect.apply);
  mark.textContent = what ? what[0]! : "·";
  return mark;
}

/** Pick the chip art: skill sheet, then shower raster, then lettered glyph. */
export function affectArtNode(
  affect: AffectModel,
  sources: ReadonlyMap<number, AffectSource>,
  doc: Document,
): HTMLElement {
  const fromBar = sources.get(affect.source);
  if (fromBar) {
    const art = skillIconNode(fromBar.icon, 0, doc);
    if (art) return art;
  }
  const skillName = SKILL_ICONS.get(affect.source);
  if (skillName) {
    const art = skillIconNode(skillName, 0, doc);
    if (art) return art;
  }
  const raster = affectRasterUrl(affect);
  if (raster) return rasterNode(raster, doc);
  return glyphNode(affect, doc);
}

/** The affects one shower image stands for, in first-seen order: one per
 *  source, which is a skill's flag or a NEW_AFFECT type
 *  (uiaffectshower.py:472-473, :626-627), except a mall affect, one per point
 *  (`MALL_DESC_IDX_START + pointIdx`, :467-468). */
export function affectChips(affects: readonly AffectModel[]): readonly (readonly AffectModel[])[] {
  const chips = new Map<string, AffectModel[]>();
  for (const affect of affects) {
    const key = affect.source === AFFECT_MALL ? `${affect.source}:${affect.apply}` : String(affect.source);
    const chip = chips.get(key);
    if (chip === undefined) chips.set(key, [affect]);
    else chip.push(affect);
  }
  return [...chips.values()];
}

export function fingerprintOf(affects: readonly AffectModel[]): string {
  return affects.map((a) => `${a.source}:${a.apply}:${a.value}`).join("|");
}

export class AffectsPanel {
  private readonly root: HTMLElement;
  private affects: readonly AffectModel[] = [];
  private sources: ReadonlyMap<number, AffectSource> = new Map();
  private fingerprint = "";
  private readonly counters = new Map<number, HTMLElement>();

  constructor(
    container: HTMLElement,
    private readonly tickHz = 20,
  ) {
    const doc = container.ownerDocument ?? document;
    installStyle(doc);

    this.root = elem(doc, "div", "affects");
    this.root.hidden = true;
    container.append(this.root);
  }

  /** The server stated every affect on this character. Absent means gone: the
   *  frame is the whole list, so an affect that stops being in it has expired
   *  or been dispelled and its chip goes away. */
  update(
    affects: readonly AffectModel[],
    sources: readonly AffectSource[] = [],
  ): void {
    this.affects = affects;
    this.sources = sourcesFrom(sources);
    this.root.hidden = affects.length === 0;

    const next = fingerprintOf(affects);
    if (next !== this.fingerprint) {
      this.fingerprint = next;
      this.rebuild();
    }
    this.paintCounters();
  }

  tick(elapsedTicks: number): void {
    if (elapsedTicks <= 0 || this.affects.length === 0) return;
    this.affects = this.affects.map((a) => ({
      ...a,
      remaining: a.remaining - elapsedTicks,
    }));
    this.paintCounters();
  }

  private rebuild(): void {
    const doc = this.root.ownerDocument;
    this.root.replaceChildren();
    this.counters.clear();

    affectChips(this.affects).forEach((group, index) => {
      const affect = group[0]!;
      const chip = elem(doc, "div", "affect");
      chip.title = [
        describe(affect, this.sources, this.tickHz),
        ...group.slice(1).flatMap((more) => {
          const what = applyName(more.apply);
          return what === null ? [] : [`${what} ${signed(more.value)}`];
        }),
      ].join(" · ");
      chip.append(affectArtNode(affect, this.sources, doc));

      const left = elem(doc, "span", "affect-left");
      chip.append(left);
      this.counters.set(index, left);

      this.root.append(chip);
    });
  }

  private paintCounters(): void {
    affectChips(this.affects).forEach((group, index) => {
      const node = this.counters.get(index);
      if (node === undefined) return;
      node.textContent = durationLabel(Math.max(...group.map((a) => a.remaining)), this.tickHz);
    });
  }
}
